import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Calendar,
  DollarSign,
  Users,
  MessageSquare,
  FileText,
  Mail,
  Phone,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  RefreshCw,
  LogOut,
  Star,
  ThumbsUp,
  ThumbsDown,
  Trash2
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { format } from "date-fns";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusColors = {
  pending: "bg-yellow-100 text-yellow-800",
  confirmed: "bg-green-100 text-green-800",
  in_progress: "bg-blue-100 text-blue-800",
  completed: "bg-emerald-100 text-emerald-800",
  cancelled: "bg-red-100 text-red-800",
  new: "bg-blue-100 text-blue-800",
  read: "bg-gray-100 text-gray-800",
  responded: "bg-green-100 text-green-800",
  closed: "bg-gray-100 text-gray-800",
  paid: "bg-green-100 text-green-800",
  unpaid: "bg-red-100 text-red-800",
  queued: "bg-yellow-100 text-yellow-800",
  sent: "bg-green-100 text-green-800",
  scheduled: "bg-blue-100 text-blue-800"
};

export default function AdminPage() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [credentials, setCredentials] = useState({ username: "", password: "" });
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [payments, setPayments] = useState([]);
  const [emails, setEmails] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [testimonialSubmissions, setTestimonialSubmissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("dashboard");

  const authHeader = {
    headers: {
      Authorization: `Basic ${btoa(`${credentials.username}:${credentials.password}`)}`
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/stats`, {
        headers: {
          Authorization: `Basic ${btoa(`${credentials.username}:${credentials.password}`)}`
        }
      });
      setStats(response.data);
      setIsAuthenticated(true);
      toast.success("Login successful!");
      fetchAllData();
    } catch (error) {
      toast.error("Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setCredentials({ username: "", password: "" });
    toast.success("Logged out");
  };

  const fetchAllData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, bookingsRes, contactsRes, quotesRes, paymentsRes, emailsRes, remindersRes, testimonialsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, authHeader),
        axios.get(`${API}/admin/bookings`, authHeader),
        axios.get(`${API}/admin/contacts`, authHeader),
        axios.get(`${API}/admin/quotes`, authHeader),
        axios.get(`${API}/admin/payments`, authHeader),
        axios.get(`${API}/admin/emails`, authHeader),
        axios.get(`${API}/admin/sms-reminders`, authHeader),
        axios.get(`${API}/admin/testimonials`, authHeader)
      ]);
      
      setStats(statsRes.data);
      setBookings(bookingsRes.data);
      setContacts(contactsRes.data);
      setQuotes(quotesRes.data);
      setPayments(paymentsRes.data);
      setEmails(emailsRes.data);
      setReminders(remindersRes.data);
      setTestimonialSubmissions(testimonialsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      if (error.response?.status === 401) {
        setIsAuthenticated(false);
        toast.error("Session expired. Please login again.");
      }
    } finally {
      setLoading(false);
    }
  }, [authHeader]);

  const updateBookingStatus = async (bookingId, status) => {
    try {
      await axios.put(`${API}/admin/bookings/${bookingId}/status?status=${status}`, {}, authHeader);
      toast.success("Status updated");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const updateContactStatus = async (contactId, status) => {
    try {
      await axios.put(`${API}/admin/contacts/${contactId}/status?status=${status}`, {}, authHeader);
      toast.success("Status updated");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const approveTestimonial = async (testimonialId) => {
    try {
      await axios.put(`${API}/admin/testimonials/${testimonialId}/approve`, {}, authHeader);
      toast.success("Testimonial approved and now visible on the website");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to approve testimonial");
    }
  };

  const rejectTestimonial = async (testimonialId) => {
    try {
      await axios.put(`${API}/admin/testimonials/${testimonialId}/reject`, {}, authHeader);
      toast.success("Testimonial rejected");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to reject testimonial");
    }
  };

  const deleteTestimonial = async (testimonialId) => {
    if (!window.confirm("Are you sure you want to delete this testimonial?")) return;
    try {
      await axios.delete(`${API}/admin/testimonials/${testimonialId}`, authHeader);
      toast.success("Testimonial deleted");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to delete testimonial");
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchAllData();
    }
  }, [isAuthenticated, fetchAllData]);

  if (!isAuthenticated) {
    return (
      <div data-testid="admin-login" className="min-h-screen bg-[#FAFAF9] flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-bold text-[#064E3B]">Admin Login</CardTitle>
            <p className="text-[#78716C]">Toronto Premium Cleaning Dashboard</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="username">Username</Label>
                <Input
                  id="username"
                  value={credentials.username}
                  onChange={(e) => setCredentials(prev => ({ ...prev, username: e.target.value }))}
                  required
                  data-testid="admin-username"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={credentials.password}
                  onChange={(e) => setCredentials(prev => ({ ...prev, password: e.target.value }))}
                  required
                  data-testid="admin-password"
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-[#064E3B] hover:bg-[#065F46]"
                disabled={loading}
                data-testid="admin-login-btn"
              >
                {loading ? "Logging in..." : "Login"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#FAFAF9] pt-24 pb-12">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-[#0C0A09]">Admin Dashboard</h1>
            <p className="text-[#78716C]">Manage bookings, contacts, and payments</p>
          </div>
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              onClick={fetchAllData}
              disabled={loading}
              className="border-[#064E3B] text-[#064E3B]"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              variant="outline"
              onClick={handleLogout}
              className="border-red-500 text-red-500 hover:bg-red-50"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[#78716C]">Total Bookings</p>
                    <p className="text-3xl font-bold text-[#064E3B]">{stats.total_bookings}</p>
                    <p className="text-xs text-yellow-600">{stats.pending_bookings} pending</p>
                  </div>
                  <Calendar className="w-10 h-10 text-[#C0A062]" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[#78716C]">Contacts</p>
                    <p className="text-3xl font-bold text-[#064E3B]">{stats.total_contacts}</p>
                    <p className="text-xs text-blue-600">{stats.new_contacts} new</p>
                  </div>
                  <MessageSquare className="w-10 h-10 text-[#C0A062]" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[#78716C]">Quote Requests</p>
                    <p className="text-3xl font-bold text-[#064E3B]">{stats.total_quotes}</p>
                  </div>
                  <FileText className="w-10 h-10 text-[#C0A062]" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[#78716C]">Revenue</p>
                    <p className="text-3xl font-bold text-[#064E3B]">${stats.total_revenue.toFixed(2)}</p>
                    <p className="text-xs text-yellow-600">{stats.pending_payments} pending</p>
                  </div>
                  <DollarSign className="w-10 h-10 text-[#C0A062]" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[#78716C]">Testimonials</p>
                    <p className="text-3xl font-bold text-[#064E3B]">{testimonialSubmissions.length}</p>
                    <p className="text-xs text-yellow-600">{stats.pending_testimonials} pending</p>
                  </div>
                  <Star className="w-10 h-10 text-[#C0A062]" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6 bg-white border border-[#E7E5E4] flex-wrap">
            <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
            <TabsTrigger value="bookings">Bookings ({bookings.length})</TabsTrigger>
            <TabsTrigger value="contacts">Contacts ({contacts.length})</TabsTrigger>
            <TabsTrigger value="quotes">Quotes ({quotes.length})</TabsTrigger>
            <TabsTrigger value="testimonials">
              Testimonials ({testimonialSubmissions.filter(t => t.status === 'pending').length} pending)
            </TabsTrigger>
            <TabsTrigger value="payments">Payments ({payments.length})</TabsTrigger>
            <TabsTrigger value="notifications">Notifications</TabsTrigger>
          </TabsList>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Recent Bookings</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {bookings.slice(0, 5).map((booking) => (
                      <div key={booking.id} className="flex items-center justify-between p-3 bg-[#FAFAF9] rounded-lg">
                        <div>
                          <p className="font-medium text-[#0C0A09]">{booking.name}</p>
                          <p className="text-sm text-[#78716C]">{booking.service_type} - {booking.date}</p>
                        </div>
                        <Badge className={statusColors[booking.status]}>{booking.status}</Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Recent Contacts</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {contacts.slice(0, 5).map((contact) => (
                      <div key={contact.id} className="flex items-center justify-between p-3 bg-[#FAFAF9] rounded-lg">
                        <div>
                          <p className="font-medium text-[#0C0A09]">{contact.name}</p>
                          <p className="text-sm text-[#78716C]">{contact.service_interest}</p>
                        </div>
                        <Badge className={statusColors[contact.status]}>{contact.status}</Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Bookings Tab */}
          <TabsContent value="bookings">
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-[#FAFAF9] border-b border-[#E7E5E4]">
                      <tr>
                        <th className="text-left p-4 font-medium text-[#78716C]">Customer</th>
                        <th className="text-left p-4 font-medium text-[#78716C]">Service</th>
                        <th className="text-left p-4 font-medium text-[#78716C]">Date/Time</th>
                        <th className="text-left p-4 font-medium text-[#78716C]">Address</th>
                        <th className="text-left p-4 font-medium text-[#78716C]">Payment</th>
                        <th className="text-left p-4 font-medium text-[#78716C]">Status</th>
                        <th className="text-left p-4 font-medium text-[#78716C]">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bookings.map((booking) => (
                        <tr key={booking.id} className="border-b border-[#E7E5E4] hover:bg-[#FAFAF9]">
                          <td className="p-4">
                            <div>
                              <p className="font-medium text-[#0C0A09]">{booking.name}</p>
                              <p className="text-sm text-[#78716C]">{booking.email}</p>
                              <p className="text-sm text-[#78716C]">{booking.phone}</p>
                            </div>
                          </td>
                          <td className="p-4">
                            <p className="capitalize">{booking.service_type}</p>
                            <p className="text-sm text-[#78716C] capitalize">{booking.service_tier}</p>
                          </td>
                          <td className="p-4">
                            <p>{booking.date}</p>
                            <p className="text-sm text-[#78716C]">{booking.time}</p>
                          </td>
                          <td className="p-4">
                            <p className="text-sm">{booking.address}</p>
                            <p className="text-sm text-[#78716C]">{booking.city}</p>
                          </td>
                          <td className="p-4">
                            <Badge className={statusColors[booking.deposit_paid ? 'paid' : 'unpaid']}>
                              {booking.deposit_paid ? 'Deposit Paid' : 'Unpaid'}
                            </Badge>
                          </td>
                          <td className="p-4">
                            <Badge className={statusColors[booking.status]}>{booking.status}</Badge>
                          </td>
                          <td className="p-4">
                            <Select
                              value={booking.status}
                              onValueChange={(value) => updateBookingStatus(booking.id, value)}
                            >
                              <SelectTrigger className="w-32">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="pending">Pending</SelectItem>
                                <SelectItem value="confirmed">Confirmed</SelectItem>
                                <SelectItem value="in_progress">In Progress</SelectItem>
                                <SelectItem value="completed">Completed</SelectItem>
                                <SelectItem value="cancelled">Cancelled</SelectItem>
                              </SelectContent>
                            </Select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Contacts Tab */}
          <TabsContent value="contacts">
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-[#FAFAF9] border-b border-[#E7E5E4]">
                      <tr>
                        <th className="text-left p-4 font-medium text-[#78716C]">Contact</th>
                        <th className="text-left p-4 font-medium text-[#78716C]">Service Interest</th>
                        <th className="text-left p-4 font-medium text-[#78716C]">Message</th>
                        <th className="text-left p-4 font-medium text-[#78716C]">Date</th>
                        <th className="text-left p-4 font-medium text-[#78716C]">Status</th>
                        <th className="text-left p-4 font-medium text-[#78716C]">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {contacts.map((contact) => (
                        <tr key={contact.id} className="border-b border-[#E7E5E4] hover:bg-[#FAFAF9]">
                          <td className="p-4">
                            <div>
                              <p className="font-medium text-[#0C0A09]">{contact.name}</p>
                              <p className="text-sm text-[#78716C]">{contact.email}</p>
                              <p className="text-sm text-[#78716C]">{contact.phone}</p>
                            </div>
                          </td>
                          <td className="p-4 capitalize">{contact.service_interest}</td>
                          <td className="p-4">
                            <p className="text-sm max-w-xs truncate">{contact.message}</p>
                          </td>
                          <td className="p-4 text-sm text-[#78716C]">
                            {new Date(contact.created_at).toLocaleDateString()}
                          </td>
                          <td className="p-4">
                            <Badge className={statusColors[contact.status]}>{contact.status}</Badge>
                          </td>
                          <td className="p-4">
                            <Select
                              value={contact.status}
                              onValueChange={(value) => updateContactStatus(contact.id, value)}
                            >
                              <SelectTrigger className="w-32">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="new">New</SelectItem>
                                <SelectItem value="read">Read</SelectItem>
                                <SelectItem value="responded">Responded</SelectItem>
                                <SelectItem value="closed">Closed</SelectItem>
                              </SelectContent>
                            </Select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Quotes Tab */}
          <TabsContent value="quotes">
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-[#FAFAF9] border-b border-[#E7E5E4]">
                      <tr>
                        <th className="text-left p-4 font-medium text-[#78716C]">Contact</th>
                        <th className="text-left p-4 font-medium text-[#78716C]">Service</th>
                        <th className="text-left p-4 font-medium text-[#78716C]">Property</th>
                        <th className="text-left p-4 font-medium text-[#78716C]">Frequency</th>
                        <th className="text-left p-4 font-medium text-[#78716C]">Date</th>
                        <th className="text-left p-4 font-medium text-[#78716C]">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {quotes.map((quote) => (
                        <tr key={quote.id} className="border-b border-[#E7E5E4] hover:bg-[#FAFAF9]">
                          <td className="p-4">
                            <div>
                              <p className="font-medium text-[#0C0A09]">{quote.name}</p>
                              <p className="text-sm text-[#78716C]">{quote.email}</p>
                              <p className="text-sm text-[#78716C]">{quote.phone}</p>
                            </div>
                          </td>
                          <td className="p-4 capitalize">{quote.service_type}</td>
                          <td className="p-4">
                            <p className="capitalize">{quote.property_type}</p>
                            <p className="text-sm text-[#78716C]">
                              {quote.bedrooms} bed, {quote.bathrooms} bath
                            </p>
                          </td>
                          <td className="p-4 capitalize">{quote.frequency}</td>
                          <td className="p-4 text-sm text-[#78716C]">
                            {new Date(quote.created_at).toLocaleDateString()}
                          </td>
                          <td className="p-4">
                            <Badge className={statusColors[quote.status]}>{quote.status}</Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Payments Tab */}
          <TabsContent value="payments">
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-[#FAFAF9] border-b border-[#E7E5E4]">
                      <tr>
                        <th className="text-left p-4 font-medium text-[#78716C]">Session ID</th>
                        <th className="text-left p-4 font-medium text-[#78716C]">Amount</th>
                        <th className="text-left p-4 font-medium text-[#78716C]">Booking</th>
                        <th className="text-left p-4 font-medium text-[#78716C]">Date</th>
                        <th className="text-left p-4 font-medium text-[#78716C]">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payments.map((payment) => (
                        <tr key={payment.id} className="border-b border-[#E7E5E4] hover:bg-[#FAFAF9]">
                          <td className="p-4">
                            <p className="font-mono text-sm">{payment.session_id?.slice(0, 20)}...</p>
                          </td>
                          <td className="p-4 font-medium">
                            ${payment.amount?.toFixed(2)} {payment.currency?.toUpperCase()}
                          </td>
                          <td className="p-4 text-sm">
                            {payment.booking_id ? payment.booking_id.slice(0, 8) : '-'}
                          </td>
                          <td className="p-4 text-sm text-[#78716C]">
                            {new Date(payment.created_at).toLocaleDateString()}
                          </td>
                          <td className="p-4">
                            <Badge className={statusColors[payment.payment_status]}>
                              {payment.payment_status}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Testimonials Tab */}
          <TabsContent value="testimonials">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Star className="w-5 h-5 text-[#C0A062]" />
                  Manage Testimonial Submissions
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-[#FAFAF9] border-b border-[#E7E5E4]">
                      <tr>
                        <th className="text-left p-4 font-medium text-[#78716C]">Client</th>
                        <th className="text-left p-4 font-medium text-[#78716C]">Location</th>
                        <th className="text-left p-4 font-medium text-[#78716C]">Service</th>
                        <th className="text-left p-4 font-medium text-[#78716C]">Rating</th>
                        <th className="text-left p-4 font-medium text-[#78716C]">Review</th>
                        <th className="text-left p-4 font-medium text-[#78716C]">Date</th>
                        <th className="text-left p-4 font-medium text-[#78716C]">Status</th>
                        <th className="text-left p-4 font-medium text-[#78716C]">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {testimonialSubmissions.map((testimonial) => (
                        <tr key={testimonial.id} className="border-b border-[#E7E5E4] hover:bg-[#FAFAF9]">
                          <td className="p-4">
                            <div>
                              <p className="font-medium text-[#0C0A09]">{testimonial.name}</p>
                              <p className="text-sm text-[#78716C]">{testimonial.email}</p>
                              {testimonial.phone && (
                                <p className="text-sm text-[#78716C]">{testimonial.phone}</p>
                              )}
                            </div>
                          </td>
                          <td className="p-4 text-sm">{testimonial.location}</td>
                          <td className="p-4 capitalize text-sm">{testimonial.service}</td>
                          <td className="p-4">
                            <div className="flex gap-0.5">
                              {[...Array(5)].map((_, i) => (
                                <Star
                                  key={i}
                                  className={`w-4 h-4 ${
                                    i < testimonial.rating
                                      ? "text-[#C0A062] fill-[#C0A062]"
                                      : "text-gray-300"
                                  }`}
                                />
                              ))}
                            </div>
                          </td>
                          <td className="p-4">
                            <p className="text-sm max-w-xs line-clamp-3">{testimonial.review}</p>
                          </td>
                          <td className="p-4 text-sm text-[#78716C]">
                            {new Date(testimonial.created_at).toLocaleDateString()}
                          </td>
                          <td className="p-4">
                            <Badge className={statusColors[testimonial.status]}>
                              {testimonial.status}
                            </Badge>
                          </td>
                          <td className="p-4">
                            <div className="flex gap-2">
                              {testimonial.status === "pending" && (
                                <>
                                  <Button
                                    size="sm"
                                    className="bg-green-600 hover:bg-green-700"
                                    onClick={() => approveTestimonial(testimonial.id)}
                                    title="Approve"
                                  >
                                    <ThumbsUp className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="border-red-500 text-red-500 hover:bg-red-50"
                                    onClick={() => rejectTestimonial(testimonial.id)}
                                    title="Reject"
                                  >
                                    <ThumbsDown className="w-4 h-4" />
                                  </Button>
                                </>
                              )}
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-gray-400 text-gray-500 hover:bg-gray-50"
                                onClick={() => deleteTestimonial(testimonial.id)}
                                title="Delete"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {testimonialSubmissions.length === 0 && (
                        <tr>
                          <td colSpan="8" className="p-8 text-center text-[#78716C]">
                            No testimonial submissions yet
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Notifications Tab */}
          <TabsContent value="notifications">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Mail className="w-5 h-5" />
                    Email Notifications ({emails.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {emails.map((email) => (
                      <div key={email.id} className="p-3 bg-[#FAFAF9] rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <Badge className={statusColors[email.status]}>{email.status}</Badge>
                          <span className="text-xs text-[#78716C]">
                            {new Date(email.created_at).toLocaleString()}
                          </span>
                        </div>
                        <p className="font-medium text-sm">{email.subject}</p>
                        <p className="text-xs text-[#78716C]">To: {email.recipient_email}</p>
                      </div>
                    ))}
                    {emails.length === 0 && (
                      <p className="text-center text-[#78716C] py-8">No email notifications yet</p>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Phone className="w-5 h-5" />
                    SMS Reminders ({reminders.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {reminders.map((reminder) => (
                      <div key={reminder.id} className="p-3 bg-[#FAFAF9] rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <Badge className={statusColors[reminder.status]}>{reminder.status}</Badge>
                          <span className="text-xs text-[#78716C]">
                            Scheduled: {new Date(reminder.scheduled_for).toLocaleString()}
                          </span>
                        </div>
                        <p className="text-sm">{reminder.message}</p>
                        <p className="text-xs text-[#78716C]">To: {reminder.phone}</p>
                      </div>
                    ))}
                    {reminders.length === 0 && (
                      <p className="text-center text-[#78716C] py-8">No SMS reminders scheduled</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
